import { useMutation } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCelebrateStafferPetAward } from "@/hooks/useStafferPets";
import { guacHttpClient } from "@/lib/http";
import {
  CandidacyWithProfileResponse,
  CreateCandidacyRequest,
  CreateCandidacyResponse,
  HttpResponse,
} from "@/types";

export function useCreateCandidacy() {
  const celebrateStafferPetAward = useCelebrateStafferPetAward();
  const currentCompanyId = useCurrentCompanyId();

  const mutation = useMutation({
    mutationFn: async ({
      companyId,
      createCandidacyRequest,
      currentJobId,
    }: {
      // Defaults to the company in the URL; pass explicitly to add to a job in a different company.
      companyId?: string;
      createCandidacyRequest: CreateCandidacyRequest;
      currentJobId: string;
    }) => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      const basePath = `companies/${companyId || currentCompanyId}/jobs/${currentJobId}/candidacies`;
      const createRes = await guacHttpClient.post<
        CreateCandidacyResponse,
        HttpResponse<CreateCandidacyResponse>,
        CreateCandidacyRequest
      >(basePath, createCandidacyRequest);

      const fetchRes = await guacHttpClient.get<
        CandidacyWithProfileResponse,
        HttpResponse<CandidacyWithProfileResponse>
      >(`${basePath}/${createRes.data.id}`);

      // The coin (a manual Laborup Search shortlist) is awarded server-side on
      // the create; carry it out so the caller can celebrate it.
      return {
        ...fetchRes.data,
        stafferPetAward: createRes.data.stafferPetAward ?? null,
        stafferPetGoalAward: createRes.data.stafferPetGoalAward ?? null,
      };
    },
    onSuccess: (data) => {
      celebrateStafferPetAward(data.stafferPetAward);
      celebrateStafferPetAward(data.stafferPetGoalAward);
    },
  });

  return mutation;
}
